import { useCallback, useEffect, useMemo, useState } from 'react';
import { statusesApi, jiraApi } from '../lib/api';
import { AppEvent, useAppEvents } from '../lib/appEvents';
import type { SettingsBadgeKey } from '../lib/settingsNav';

export interface SettingsBadges {
  unmappedCount: number;
  jiraModifiedCount: number;
  /** Per-key lookup, so a consumer can render `counts[item.badge]` directly. */
  counts: Record<SettingsBadgeKey, number>;
  /** Badge keys whose count is currently > 0 — drives "needs attention". */
  attentionBadges: SettingsBadgeKey[];
}

/**
 * Single source of the Settings "needs attention" counts: tickets in no board
 * column (Status Mapping — the `unmapped` key is a legacy name; see
 * {@link SettingsBadge}) + tickets edited in Avium but not yet pushed (Data
 * Sources).
 *
 * Until now each of the three Settings surfaces — the global Sidebar entry, the
 * Settings home cards, and the in-area rail (AdminLayout) — fetched these two
 * counts itself, so the API calls, the 20s poll, and the app-event wiring were
 * copy-pasted three times (and the tooltip copy drifted). This hook owns all of
 * it; the surfaces just render. Pairs with {@link SettingsBadge}, which owns the
 * badge's look + wording.
 *
 * - `enabled` (default true): when false, skips fetching and reports zeros — the
 *   Sidebar gates on `isAdmin()`.
 * - `poll` (default false): when true, re-fetches the modified count every 20s,
 *   preserving the Sidebar's long-standing freshness behavior.
 *
 * Always re-fetches on the StatusMappingChanged / JiraModifiedCountChanged app
 * events, so every consumer updates live the moment the underlying data changes
 * (an improvement for the rail + home, which previously only fetched on mount).
 */
export function useSettingsBadges(opts: { enabled?: boolean; poll?: boolean } = {}): SettingsBadges {
  const { enabled = true, poll = false } = opts;
  const [unmappedCount, setUnmappedCount] = useState(0);
  const [jiraModifiedCount, setJiraModifiedCount] = useState(0);

  const refreshUnmapped = useCallback(() => {
    if (!enabled) return;
    statusesApi.unmappedCount().then(r => setUnmappedCount(r.data.count)).catch(() => {});
  }, [enabled]);

  const refreshModified = useCallback(() => {
    if (!enabled) return;
    jiraApi.modifiedCount().then(r => setJiraModifiedCount(r.data.count)).catch(() => {});
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setUnmappedCount(0);
      setJiraModifiedCount(0);
      return;
    }
    refreshUnmapped();
    refreshModified();
    // Poll only the modified count — it changes as the user edits tickets; the
    // unmapped count only moves on an explicit mapping change (covered below).
    const interval = poll ? setInterval(refreshModified, 20000) : undefined;
    return () => { if (interval) clearInterval(interval); };
  }, [enabled, poll, refreshUnmapped, refreshModified]);

  // Live refresh when another surface reports the underlying data changed.
  useAppEvents([AppEvent.StatusMappingChanged, AppEvent.JiraModifiedCountChanged], () => {
    refreshUnmapped();
    refreshModified();
  });

  return useMemo(() => {
    const attentionBadges: SettingsBadgeKey[] = [];
    if (unmappedCount > 0) attentionBadges.push('unmapped');
    if (jiraModifiedCount > 0) attentionBadges.push('jiraModified');
    return {
      unmappedCount,
      jiraModifiedCount,
      counts: { unmapped: unmappedCount, jiraModified: jiraModifiedCount },
      attentionBadges,
    };
  }, [unmappedCount, jiraModifiedCount]);
}
